import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Header, Input, Label, Grid, Dropdown, Message, Checkbox } from 'semantic-ui-react'
import { ActionButton, LoadingWrapper, ReactTable } from "../common";
import { VacationLimitOverrides } from '../TeamCalendar/VacationLimitOverrides';
import { isICCSupport, isReadOnly, getCSRFToken, fetchWithAuth } from '../auth';
import { timezones } from '../common/timezones';
import { TeamSettingsSection } from './TeamSettingsSection';
import { getTeamSettings, isNonNegativeInteger } from '../common/teamSettings';
import { TeamMembershipSection } from './TeamMembershipSection';
import { flattenMessages } from './teamMembershipService';
import './Teams.css'

const SETTING_KEYS = [
    'suppressNotifications',
    'minimumMembersToBeReady',
    'vacationLimit',
    'submitReadiness',
]

const isBlank = (value) => value == null || value === ''

const toNullableInt = (value) => (isBlank(value) ? null : parseInt(String(value).trim(), 10))

const normalizeTeamSettings = (settings) => ({
    ...getTeamSettings(settings),
    minimumMembersToBeReady: toNullableInt(settings.minimumMembersToBeReady) ?? 0,
    vacationLimit: toNullableInt(settings.vacationLimit),
})

const withMembers = (team) => ({ ...team, members: team.members || [] })
const normalizeMemberships = (members = []) => members
    .map(({ userId, isSecondary }) => ({ userId, isSecondary: isSecondary === true }))
    .sort((left, right) => left.userId.localeCompare(right.userId));
const sameMemberships = (left = [], right = []) =>
    JSON.stringify(normalizeMemberships(left)) === JSON.stringify(normalizeMemberships(right));

const getSaveErrorMessage = async (response) => {
    if (response.status === 403) return 'You do not have permission to edit this team.'
    const body = await response.json().catch(() => null)
    const messages = flattenMessages(body).trim()
    return messages ? 'Could not save. ' + messages : 'Could not save. Please try again.'
}

export const TeamsDetails = () => {

    const [team, setTeam] = useState({ members: [], name: '' });
    const [initialTeam, setinitialTeam] = useState(null);
    const [teamTypes, setTeamTypes] = useState([]);
    const [succeeded, setSucceeded] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [loaded, setLoaded] = useState(false)
    const [saving, setSaving] = useState(false)
    const [teamTypesLoaded, setTeamTypesLoaded] = useState(false)
    const [teamSettings, setTeamSettings] = useState(() => getTeamSettings(null))
    const [staffingEnabled, setStaffingEnabled] = useState(false)
    const [ptoEnabled, setPtoEnabled] = useState(false)
    const [ptoChanged, setPtoChanged] = useState(false)
    const [vacationLimitOverrides, setVacationLimitOverrides] = useState([])
    const [overridesLoading, setOverridesLoading] = useState(false)
    const [markets, setMarkets] = useState([])
    const [marketsLoaded, setMarketsLoaded] = useState(false)
    const { id } = useParams();
    const [isLandscape, setIsLandscape] = useState(window.matchMedia('(orientation:landscape)').matches)


    const canEditTeam = Boolean(initialTeam) && loaded && team.canEditTeam === true;
    const canEditTeamLeadership = Boolean(initialTeam) && loaded && team.canEditTeamLeadership === true;
    const canEditTeamMembers = Boolean(initialTeam) && loaded && team.canEditTeamMembers === true;
    const canEditSecondaryAm = Boolean(initialTeam) && loaded && team.canEditSecondaryAm === true;
    const hasMembershipChanges = Boolean(initialTeam) && !sameMemberships(team.members, initialTeam.members);
    const canSaveMembershipChanges = (canEditTeam || canEditTeamLeadership || canEditTeamMembers || canEditSecondaryAm) && hasMembershipChanges;
    const assetRequirementRows = useMemo(() => {
        const requirements = (team.assetRequirements?.requirements || []).map(requirement => ({
            ...requirement,
            assignedAssetsSearch: (requirement.filledAssets || []).map(asset => asset.iccName).join(', ')
        }));
        const otherAssets = team.assetRequirements?.otherAssets || [];

        return otherAssets.length ? [
            ...requirements,
            {
                id: 'other-assets',
                isOtherAssetsRow: true,
                label: 'Other',
                quantity: '',
                filledAssets: [],
                openQuantity: 0,
                otherAssets,
                assignedAssetsSearch: otherAssets.map(asset => asset.iccName).join(', ')
            }
        ] : requirements;
    }, [team.assetRequirements?.requirements, team.assetRequirements?.otherAssets]);
    const assetRequirementColumns = useMemo(() => [
        {
            Header: 'Requirement',
            accessor: 'label',
            Cell: ({ row, value }) => row.original.isOtherAssetsRow ? (
                <div>
                    <strong>{value}</strong>
                    <div style={{ color: '#555', fontSize: '0.85em' }}>Not matched to a configured requirement</div>
                </div>
            ) : value
        },
        {
            Header: 'Quantity',
            accessor: 'quantity',
            Cell: ({ row, value }) => row.original.isOtherAssetsRow ? '' : value
        },
        {
            Header: 'Assigned/Filled Assets',
            accessor: 'assignedAssetsSearch',
            Cell: ({ row }) => {
                const requirement = row.original;
                if (requirement.isOtherAssetsRow) {
                    return requirement.otherAssets.map(asset => (
                        <div key={asset.id} style={{ minHeight: '1.5rem' }}>
                            <a href={`/assets/${asset.id}/view`}>{asset.iccName}</a>
                        </div>
                    ));
                }

                const slots = [
                    ...(requirement.filledAssets || []),
                    ...Array(Number(requirement.openQuantity) || 0).fill(null)
                ];

                return slots.map((asset, index) => (
                    <div
                        key={asset?.id ?? `open-${index}`}
                        data-testid={asset ? undefined : 'open-asset-slot'}
                        style={{ minHeight: '1.5rem' }}
                    >
                        {asset && <a href={`/assets/${asset.id}/view`}>{asset.iccName}</a>}
                    </div>
                ));
            }
        }
    ], []);

    useEffect(() => {
        const updateLandscape = () => {
            setIsLandscape(window.matchMedia('(orientation:landscape)').matches)
        }
        window.addEventListener('resize', updateLandscape)
        return () => window.removeEventListener('resize', updateLandscape)
    }, [])


    useEffect(() => {
        fetchTeamDetails();
        fetchTeamTypes();
        fetchMarkets();
        if (isICCSupport()) fetchVacationLimitOverrides();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchVacationLimitOverrides = async () => {
        setOverridesLoading(true);
        try {
            const resp = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}vacation-limit-overrides/?team=${id}`,
                { headers: { 'X-CSRFToken': getCSRFToken() }, credentials: 'include' }
            );
            if (resp.ok) {
                setVacationLimitOverrides(await resp.json());
            }
        } finally {
            setOverridesLoading(false);
        }
    };

    const teamUrl = process.env.REACT_APP_ICC_TOOL_API_HOST + `teams/${id}/`

    const patchTeam = (payload) => fetch(teamUrl, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken()
        },
        body: JSON.stringify(payload),
    })

    const requestTeam = () => fetch(teamUrl, {
        headers: { 'X-CSRFToken': getCSRFToken() },
        credentials: 'include',
    })

    const applyTeam = (data) => {
        setTeam(data)
        setinitialTeam(data)
        setTeamSettings(getTeamSettings(data))
        setStaffingEnabled(data.minimumMembersToBeReady > 0)
        setPtoEnabled(data.vacationLimit !== null)
        setPtoChanged(false)
    }

    const reloadTeam = async () => {
        const response = await requestTeam()
        if (!response.ok) throw new Error('Could not reload team')
        return withMembers(await response.json())
    }

    const fetchTeamDetails = async () => {
        setLoaded(false)
        setinitialTeam(null)
        setErrorMessage('')
        try {
            const response = await requestTeam();
            if (!response.ok) {
                setErrorMessage(response.status === 403 ? 'You do not have permission to view this team.' : 'Could not load this team. Please try again.')
                return
            }
            const respJson = await response.json();
            if (SETTING_KEYS.some(key => respJson[key] === undefined)) {
                setErrorMessage('Team details are missing required settings. Editing is unavailable.')
                return
            }
            applyTeam(withMembers(respJson))
        } catch {
            setErrorMessage('Could not load this team. Please try again.')
        } finally {
            setLoaded(true)
        }
    };

    const fetchTeamTypes = async () => {
        setTeamTypesLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'team-types/', {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        const data = await response.json();
        setTeamTypes(data);
        setTeamTypesLoaded(true)
    };

    const fetchMarkets = async () => {
        setMarketsLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'teams/markets/', {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        const data = await response.json();
        setMarkets(data);
        setMarketsLoaded(true)
    };


    function handleChange(key, value) {
        const teamCopy = structuredClone(team)
        teamCopy[key] = value
        setTeam(teamCopy)
    }

    const handleTeamNameChange = (e) => {
        setTeam({ ...team, name: e.target.value });
    };

    const handleMembersChange = (members) => {
        setTeam(prev => ({ ...prev, members }))
        setSucceeded(false)
        setErrorMessage('')
    };

    const handleCancel = () => {
        setTeam({ ...initialTeam });
        setTeamSettings(getTeamSettings(initialTeam));
        setStaffingEnabled(initialTeam.minimumMembersToBeReady > 0);
        setPtoEnabled(initialTeam.vacationLimit !== null);
        setPtoChanged(false);
    };

    const handleSettingsChange = (changes) => {
        if (Object.prototype.hasOwnProperty.call(changes, 'vacationLimit')) setPtoChanged(true)
        setTeamSettings(prev => ({ ...prev, ...changes }))
    };

    const handlePtoChange = (enabled) => {
        setPtoEnabled(enabled)
        setPtoChanged(true)
    };

    const buildPatchPayload = () => {
        const payload = {}
        if (team.name !== initialTeam.name) payload.name = team.name
        if (team.timezone !== initialTeam.timezone) payload.timezone = team.timezone
        if ((team.isActive ?? true) !== (initialTeam.isActive ?? true)) payload.isActive = team.isActive ?? true
        if ((team.market?.id ?? null) !== (initialTeam.market?.id ?? null)) payload.marketId = team.market?.id ?? null
        if ((team.teamType?.id ?? null) !== (initialTeam.teamType?.id ?? null)) payload.teamTypeId = team.teamType?.id ?? null

        const next = normalizeTeamSettings(teamSettings)
        next.minimumMembersToBeReady = staffingEnabled ? Number(teamSettings.minimumMembersToBeReady) : 0
        next.vacationLimit = ptoEnabled ? Number(teamSettings.vacationLimit) : null
        const previous = getTeamSettings(initialTeam)
        SETTING_KEYS.forEach(key => {
            if (next[key] !== previous[key]) payload[key] = next[key]
        })
        if (team.isActive === false) {
            delete payload.suppressNotifications
        }
        if (team.isActive === false && initialTeam.suppressNotifications !== true) {
            payload.suppressNotifications = true
        }

        return payload
    }

    const handleUpdateTeam = async () => {
        if (saving || (!canEditTeam && !canSaveMembershipChanges) || !initialTeam) return
        const { minimumMembersToBeReady, vacationLimit } = teamSettings
        if (canEditTeam && staffingEnabled && (!isNonNegativeInteger(minimumMembersToBeReady) || Number(minimumMembersToBeReady) <= 0)) {
            setErrorMessage('Minimum members to be ready must be a positive integer when enabled.')
            return
        }
        const preservingBlockedPto = initialTeam.vacationLimit === 0 && !ptoChanged && vacationLimit === 0
        if (canEditTeam && ptoEnabled && !preservingBlockedPto && (!isNonNegativeInteger(vacationLimit) || Number(vacationLimit) <= 0)) {
            setErrorMessage('Daily PTO limit must be a positive integer when enabled.')
            return
        }

        setSaving(true)
        setErrorMessage('')
        setSucceeded(false)

        const fieldPayload = canEditTeam ? buildPatchPayload() : {}
        const shouldPatchFields = canEditTeam && (Object.keys(fieldPayload).length > 0 || !canSaveMembershipChanges)
        let membersSaved = false
        try {
            if (canSaveMembershipChanges) {
                const response = await patchTeam({ members: normalizeMemberships(team.members) })
                if (!response.ok) {
                    setErrorMessage(await getSaveErrorMessage(response))
                    return
                }
                membersSaved = true
            }
            if (shouldPatchFields) {
                const response = await patchTeam(fieldPayload)
                if (!response.ok) {
                    setErrorMessage(await getSaveErrorMessage(response))
                    if (membersSaved) {
                        // Keep staged field edits, but don't resend the saved roster on retry.
                        const { members } = await reloadTeam()
                        setinitialTeam(prev => ({ ...prev, members }))
                        setTeam(prev => ({ ...prev, members }))
                    }
                    return
                }
            }
            // The backend may reassign supervisors, so reload instead of trusting the PATCH body.
            applyTeam(await reloadTeam())
            setSucceeded(true)
        } catch {
            setErrorMessage(membersSaved
                ? 'Saved, but the team could not be reloaded. Please refresh the page.'
                : 'Could not save. Please try again.')
        } finally {
            window.scrollTo({ top: 0, behavior: 'smooth' })
            setSaving(false)
        }
    };


    return (
        <LoadingWrapper text="Loading team" loading={!loaded}>
            <div align="center">
                {succeeded && <Message style={{ width: "75%" }} positive>Saved successfully!</Message>}
                {errorMessage !== '' && <Message style={{ width: "75%" }} negative>{errorMessage}</Message>}
                <div>
                    <div>
                        <Header size='medium'>Team: {team.name}</Header>
                    </div>
                    <div>
                        <Grid centered>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='center'></Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Team Name</Label></Grid.Column>
                                <Grid.Column width={8}>
                                    <Input
                                        type="text"
                                        id="teamName"
                                        value={team.name}
                                        onChange={handleTeamNameChange}
                                        readOnly={!canEditTeam}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Market</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Dropdown
                                        disabled={!canEditTeam}
                                        loading={!marketsLoaded}
                                        selection
                                        search
                                        clearable
                                        placeholder="Select market"
                                        options={markets.map((m) => ({
                                            key: m.id,
                                            value: m.id,
                                            text: m.label
                                        }))}
                                        value={team.market?.id ?? null}
                                        onChange={(ev, data) => {
                                            const newMarket = markets.find(m => m.id === data.value) || null
                                            handleChange('market', newMarket)
                                        }}
                                        style={{ width: isLandscape ? "50%" : "100%" }}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Asset Requirements</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    {assetRequirementRows.length ? (
                                        <ReactTable
                                            className="team-asset-requirements-table react-table-mobile-compact"
                                            data={assetRequirementRows}
                                            columns={assetRequirementColumns}
                                            hideGlobalFilter
                                            getRowProps={row => row.original.isOtherAssetsRow ? {
                                                className: 'team-asset-requirements-other-row',
                                                style: { backgroundColor: '#f2f2f2' }
                                            } : {}}
                                        />
                                    ) : (
                                        <p>No asset requirements configured for this team.</p>
                                    )}
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Team Type</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Dropdown
                                        disabled={!canEditTeam}
                                        loading={!teamTypesLoaded}
                                        selection
                                        search
                                        clearable
                                        placeholder="Select team type"
                                        options={teamTypes.map((type) => ({
                                            key: type.id,
                                            value: type.id,
                                            text: type.name
                                        }))}
                                        value={team.teamType?.id || null}
                                        onChange={(ev, data) => {
                                            const newType = teamTypes.find(t => t.id === data.value) || null
                                            handleChange('teamType', newType)
                                        }}
                                        style={{ width: isLandscape ? "50%" : "100%" }}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            {initialTeam && <TeamSettingsSection
                                team={team}
                                settings={teamSettings}
                                staffingEnabled={staffingEnabled}
                                onStaffingChange={setStaffingEnabled}
                                ptoEnabled={ptoEnabled}
                                onPtoChange={handlePtoChange}
                                onChange={handleSettingsChange}
                                disabled={!canEditTeam}
                            />}
                            {!isReadOnly() && isICCSupport() && initialTeam && (
                                <Grid.Row>
                                    <Grid.Column width={4} textAlign='right'>
                                        <Label size="large" active pointing="right">Date Overrides</Label>
                                    </Grid.Column>
                                    <Grid.Column width={8}>
                                        <VacationLimitOverrides
                                            lockedTeam={{ id: team.id, name: team.name }}
                                            overrides={vacationLimitOverrides}
                                            loading={overridesLoading}
                                            onOverrideAdded={newOverride =>
                                                setVacationLimitOverrides(prev => [...prev, newOverride])
                                            }
                                            onOverrideDeleted={deletedId =>
                                                setVacationLimitOverrides(prev => prev.filter(o => o.id !== deletedId))
                                            }
                                        />
                                    </Grid.Column>
                                </Grid.Row>
                            )}
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Timezone</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Dropdown
                                        selection
                                        search
                                        disabled={!canEditTeam}
                                        options={timezones.map((timezone) => {
                                            return {
                                                key: timezone.abbr,
                                                value: timezone.abbr,
                                                text: `${timezone.value} (${timezone.abbr})`
                                            }
                                        })}
                                        value={team.timezone}
                                        onChange={(ev, data) => handleChange('timezone', data.value)}
                                        style={{ width: isLandscape ? "50%" : "100%" }}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Team Status</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Checkbox
                                        data-testid="team-active-toggle"
                                        toggle
                                        label={team.isActive === false ? 'Inactive' : 'Active'}
                                        checked={team.isActive !== false}
                                        onChange={(e, data) => handleChange('isActive', data.checked)}
                                        disabled={!canEditTeam}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            <TeamMembershipSection
                                members={team.members}
                                canEditTeam={canEditTeam}
                                canEditTeamMembers={canEditTeamMembers}
                                canEditTeamLeadership={canEditTeamLeadership}
                                canEditSecondaryAm={canEditSecondaryAm}
                                disabled={saving}
                                onMembersChange={handleMembersChange}
                            />
                            {(canEditTeam || canSaveMembershipChanges) && (
                                <Grid.Row centered>
                                    <ActionButton submitAction={handleUpdateTeam}
                                        resetHide={false}
                                        resetText={'Cancel'}
                                        showConfirmation={true}
                                        resetAction={handleCancel}
                                        disabled={saving}
                                        submitText={saving ? 'Saving...' : 'Save'}
                                    />
                                </Grid.Row>
                            )}
                        </Grid>
                    </div>
                </div>
            </div>
        </LoadingWrapper>
    )

};
